// ── Vehicle Accident / Insurance Claim — PDF engine ───────────────────────────
// Two jobs, both client-side (pdf-lib, same as leaseContract.js):
//   1. buildAccidentSummaryPdfBytes(claim) → a Hollypaul-branded accident-report
//      summary we fully control (always works, regardless of the insurer form).
//   2. fillAllianzForm(templateBytes, claim, mapping?) → fills the AcroForm fields of
//      an uploaded Allianz claim PDF. Field names on insurer forms are unknowable up
//      front, so we auto-map by keyword and let the admin correct the mapping. If the
//      uploaded PDF has NO fillable fields (a flat/scanned form), listPdfFields returns
//      [] and the UI falls back to the branded summary.
//
// Insured/insurer details are the INSURED_NAME / POLICY_NO constants below.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date)) return String(d);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

const nonEmpty = (v) => v != null && String(v).trim() !== '';

// ── 1. Branded summary PDF ────────────────────────────────────────────────────
function summaryBlocks(c) {
  const tp = c.thirdParty || {};
  const B = [];
  B.push({ kind: 'h', text: 'INCIDENT' });
  B.push({ kind: 'kv', label: 'Vehicle (rego)', value: c.rego || '—' });
  B.push({ kind: 'kv', label: 'Driver', value: [c.driverName, c.driverLogin && `(${c.driverLogin})`].filter(Boolean).join(' ') || '—' });
  B.push({ kind: 'kv', label: 'Date & time', value: [fmtDate(c.incidentDate), c.incidentTime].filter(nonEmpty).join(' · ') || '—' });
  B.push({ kind: 'kv', label: 'Location', value: c.location || '—' });
  if (nonEmpty(c.latitude) && nonEmpty(c.longitude)) {
    B.push({ kind: 'kv', label: 'GPS', value: `${c.latitude}, ${c.longitude}` });
  }
  B.push({
    kind: 'kv',
    label: 'Police',
    value: c.policeNotified
      ? `Notified${c.policeAttended ? ', attended the scene' : ', did not attend'}${c.policeRef ? ` — event ${c.policeRef}` : ''}`
      : 'Not notified',
  });

  B.push({ kind: 'h', text: 'WHAT HAPPENED' });
  B.push({ kind: 'p', text: c.description || '—' });

  if (nonEmpty(c.damage)) {
    B.push({ kind: 'h', text: 'DAMAGE TO OUR VEHICLE' });
    B.push({ kind: 'p', text: c.damage });
  }
  if (nonEmpty(c.injuries)) {
    B.push({ kind: 'h', text: 'INJURIES / PEOPLE INVOLVED' });
    B.push({ kind: 'p', text: c.injuries });
  }

  const hasTp = ['name', 'phone', 'rego', 'make', 'model', 'insurer', 'policy', 'notes'].some((k) => nonEmpty(tp[k]));
  if (hasTp) {
    B.push({ kind: 'h', text: 'THIRD PARTY' });
    if (nonEmpty(tp.name)) B.push({ kind: 'kv', label: 'Name', value: tp.name });
    if (nonEmpty(tp.phone)) B.push({ kind: 'kv', label: 'Phone', value: tp.phone });
    const tpVeh = [tp.make, tp.model, tp.rego].filter(nonEmpty).join(' · ');
    if (tpVeh) B.push({ kind: 'kv', label: 'Vehicle', value: tpVeh });
    if (nonEmpty(tp.insurer)) B.push({ kind: 'kv', label: 'Insurer', value: tp.insurer });
    if (nonEmpty(tp.policy)) B.push({ kind: 'kv', label: 'Policy no.', value: tp.policy });
    if (nonEmpty(tp.notes)) B.push({ kind: 'p', text: tp.notes });
  }

  B.push({ kind: 'h', text: 'CLAIM' });
  B.push({ kind: 'kv', label: 'Insured', value: `${INSURED_NAME} · ACN ${INSURED_ACN}` });
  B.push({ kind: 'kv', label: 'Address', value: INSURED_ADDRESS });
  B.push({ kind: 'kv', label: 'Insurer', value: 'Allianz Australia Insurance Limited · Motor Fleet' });
  B.push({ kind: 'kv', label: 'Policy no.', value: POLICY_NO });
  B.push({ kind: 'kv', label: 'Broker', value: BROKER });
  B.push({ kind: 'kv', label: 'Claim number', value: c.claimRef || '(awaiting)' });
  B.push({ kind: 'kv', label: 'Status', value: (c.status || 'reported').toUpperCase() });
  // A loss outside the schedule's period isn't covered by this policy — say so on the
  // face of the document rather than let it reach the insurer unnoticed.
  if (c.incidentDate && (c.incidentDate < POLICY_FROM || c.incidentDate > POLICY_TO)) {
    B.push({ kind: 'p', bold: true, text: `NOTE: the incident date falls OUTSIDE the current period of insurance (${POLICY_FROM} to ${POLICY_TO}). Check which policy period responds before lodging.` });
  }

  B.push({ kind: 'rule' });
  B.push({ kind: 'centre', text: 'Generated by the Hollypaul App Engine — accident report summary for insurance purposes.', size: 7 });
  return B;
}

export async function buildAccidentSummaryPdfBytes(claim) {
  const blocks = summaryBlocks(claim || {});
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 595.28, PH = 841.89, M = 54, FOOT = 48;
  const colW = PW - M * 2;
  const GOLD = rgb(0.788, 0.659, 0.298);
  const INK = rgb(0.067, 0.067, 0.078);
  const GREY = rgb(0.5, 0.5, 0.5);

  let page = pdf.addPage([PW, PH]);

  const BAND = 92;
  page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: INK });
  page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
  page.drawText('HOLLYPAUL', { x: M, y: PH - 46, size: 25, font: bold, color: GOLD });
  page.drawText('FLEET · ACCIDENT REPORT', { x: M, y: PH - 64, size: 9, font, color: rgb(0.85, 0.85, 0.9) });
  const hTitle = 'ACCIDENT / CLAIM SUMMARY';
  page.drawText(hTitle, { x: PW - M - bold.widthOfTextAtSize(hTitle, 13), y: PH - 44, size: 13, font: bold, color: rgb(1, 1, 1) });
  const hSub = `Prepared ${fmtDate(new Date())}`;
  page.drawText(hSub, { x: PW - M - font.widthOfTextAtSize(hSub, 8.5), y: PH - 60, size: 8.5, font, color: rgb(0.7, 0.7, 0.78) });

  let y = PH - BAND - 22;
  const ensure = (need) => { if (y - need < FOOT) { page = pdf.addPage([PW, PH]); y = PH - M; } };
  const wrap = (text, f, size, maxW) => {
    const words = String(text).split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };
  const drawText = (text, { f = font, size = 9.5, indent = 0, color = rgb(0.12, 0.12, 0.12), gap = 4, align = 'left' } = {}) => {
    const maxW = colW - indent;
    for (const ln of wrap(text, f, size, maxW)) {
      ensure(size * 1.4);
      let x = M + indent;
      if (align === 'centre') x = M + (colW - f.widthOfTextAtSize(ln, size)) / 2;
      page.drawText(ln, { x, y: y - size, size, font: f, color });
      y -= size * 1.4;
    }
    y -= gap;
  };

  for (const b of blocks) {
    switch (b.kind) {
      case 'h':
        ensure(26); y -= 8;
        drawText(b.text, { f: bold, size: 11, gap: 3, color: INK });
        page.drawRectangle({ x: M, y: y + 1, width: 38, height: 2, color: GOLD });
        y -= 8;
        break;
      case 'p':
        drawText(b.text, { size: 9.5, gap: 5 });
        break;
      case 'kv': {
        ensure(15);
        page.drawText(`${b.label}:`, { x: M, y: y - 9.5, size: 9.5, font: bold, color: rgb(0.12, 0.12, 0.12) });
        const vx = M + 150;
        for (const ln of wrap(b.value, font, 9.5, colW - 150)) {
          ensure(13);
          page.drawText(ln, { x: vx, y: y - 9.5, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
          y -= 13;
        }
        y -= 2;
        break;
      }
      case 'rule':
        ensure(12); y -= 4;
        page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.8, color: rgb(0.7, 0.7, 0.7) });
        y -= 10;
        break;
      case 'centre':
        drawText(b.text, { size: b.size || 9.5, align: 'centre', gap: 3, color: GREY });
        break;
      default: break;
    }
  }

  const pages = pdf.getPages();
  const total = pages.length;
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: M, y: 38 }, end: { x: PW - M, y: 38 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    pg.drawText('HOLLYPAUL · Fleet accident report', { x: M, y: 27, size: 7, font, color: GREY });
    const pn = `Page ${i + 1} of ${total}`;
    pg.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: 27, size: 7, font, color: GREY });
  });

  return pdf.save();
}

// ── 2. Allianz claim form (CLM002 01/04) ──────────────────────────────────────
// Allianz's "Motor Vehicle Claim Form" is a FLAT 4-page A4 PDF — it has no AcroForm
// fields, so there is nothing to setText() on. We fill it the only way that works:
// by drawing our values onto the page at coordinates measured from the form's own
// printed labels (see FIELDS below — every x/y was taken from the real CLM002).
//
// The AcroForm path is kept first: if Allianz ever ships a fillable revision, that
// wins automatically and the overlay is never used.
//
// Coordinates: measured top-left (as the label sits on the page); pdf-lib draws from
// bottom-left, so y is converted with `pageH - (labelY + BASE)`. Values are placed on
// the same baseline as their printed label, just to the right of it.

const A4_W = 595, A4_H = 842;
const BASE = 8;            // label top → text baseline
const INK = () => rgb(0.05, 0.15, 0.5); // blue, so a reader can see what we filled in

// All confirmed against the Marsh schedule (invoice 030-2226161), 2026-07-17.
export const INSURED_NAME = 'PH Bouveng Pty Ltd';
export const INSURED_ACN = '093 039 297';
export const INSURED_ABN = '12 093 039 297';
// PH Bouveng is GST-registered and the van is used wholly for the delivery business,
// so the full GST on both the policy and the vehicle is claimable as an input tax credit.
export const INSURED_ITC_PCT = '100';
export const INSURED_ADDRESS = '25 Gilwinga Drive, Bayview NSW 2104';
// The POLICY NUMBER. Do not confuse with "POL428BA 12/21", which is the Allianz Motor
// Fleet policy WORDING document — an earlier guess used that by mistake, which would
// have sent claims quoting a document reference instead of the policy.
export const POLICY_NO = '1310100389VFT';
export const POLICY_WORDING = 'Allianz Motor Fleet Insurance POL428BA 12/21';
// Period of insurance on the current schedule — a claim dated outside this isn't covered
// by it, so the claim form flags it rather than quoting a lapsed policy.
export const POLICY_FROM = '2026-06-30';
export const POLICY_TO = '2027-06-30';
export const BROKER = 'Marsh';
export const INSURED_POSTCODE = '2104';
export const INSURED_STREET = '25 Gilwinga Drive, Bayview NSW';
// Who Allianz should talk to about a claim, and what the schedule says we do.
export const INSURED_CONTACT = 'Paul Bouveng';
export const INSURED_EMAIL = 'paul@hollypaul.com';
export const INSURED_BUSINESS = 'Parcel and letter delivery set routes';
// Basic excess from the schedule. Age/inexperienced excesses stack on top of this
// ($850 under 21, $500 for 21–25, $500 inexperienced) — not asserted here because it
// depends on the driver, and the form only has one Excess box.
export const BASIC_EXCESS = '2,500';

// The blank Allianz CLM002, bundled so filling needs no upload. Served from public/.
export const CLM002_URL = '/forms/allianz-clm002.pdf';

// Split "1/52 Newman Street, Merrylands, NSW, 2160" → street part + postcode, because
// the form has a separate Postcode box.
export function splitPostcode(addr) {
  const s = (addr || '').toString().trim();
  const m = /(.*?)[,\s]*\b(\d{4})\b\s*$/.exec(s);
  if (!m) return { street: s, postcode: '' };
  return { street: m[1].replace(/[,\s]+$/, ''), postcode: m[2] };
}

const ddmmyyyy = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00' : ''));
  if (isNaN(d)) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};
// "14:35" → { time: "2:35", ampm: "pm" } — the form has a separate am/pm box.
const splitAmPm = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return { time: hhmm || '', ampm: '' };
  const h = Number(m[1]);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { time: `${h12}:${m[2]}`, ampm };
};

// Every value we can place on the real CLM002, keyed by page index.
//   { x, y, get } → single-line text drawn at (x, y-baseline)
//   wrap: n       → wrap across n px width into `lines` rows, `lh` apart
//   tick: true    → draw an X in a checkbox at (x, y)
// `veh`    — the fleet vehicle {year, make, model, owner}
// `drv`    — the driver's record {contact, address, mobile, company_name, type}
function clm002Fields(c, veh, drv) {
  const tp = c.thirdParty || {};
  const t = splitAmPm(c.incidentTime);
  const driver = [c.driverName, c.driverLogin && `(${c.driverLogin})`].filter(Boolean).join(' ');
  const dAddr = splitPostcode(drv?.address);
  const loc = splitPostcode(c.location);
  // The vehicle is a company van, so the registered owner is the insured. Only say so
  // when the fleet actually records it as ours — an owner-driver's van is not.
  const ownedByUs = /^phb$/i.test((veh?.owner || '').trim());
  // The form asks "who was at fault and why". We can only state the driver's account,
  // and we attribute it as such rather than editorialising on liability.
  const faultAccount = c.description
    ? `Per our driver's account: the third-party vehicle changed lanes into the lane already occupied by the insured vehicle and struck it. See the full description on the previous page.`
    : '';
  return [
    // ── Page 1 — insured, policy, vehicle, driver ──
    { p: 0, x: 115, y: 139.6, v: c.claimRef },                              // Claim Number
    { p: 0, x: 115, y: 161.1, v: INSURED_NAME },                            // Name of Insured
    { p: 0, x: 100, y: 176.6, v: INSURED_BUSINESS },                        // Occupation
    { p: 0, x: 115, y: 192.1, v: INSURED_CONTACT },                         // Contact Person
    { p: 0, x: 80,  y: 223.1, v: INSURED_EMAIL },                           // Email
    { p: 0, x: 100, y: 238.6, v: BROKER },                                  // Broker/Agent
    { p: 0, x: 105, y: 254.1, v: INSURED_STREET },                          // Postal Address
    { p: 0, x: 458, y: 269.6, v: INSURED_POSTCODE },                        // Postcode
    { p: 0, x: 90,  y: 285.1, v: POLICY_NO },                               // Policy No.
    { p: 0, x: 442, y: 285.1, v: BASIC_EXCESS },                            // Excess $
    { p: 0, x: 105, y: 300.6, v: ddmmyyyy(POLICY_FROM) },                   // Inception Date
    { p: 0, x: 285, y: 300.6, v: ddmmyyyy(POLICY_TO) },                     // Expiry Date
    // G.S.T. block — registered Yes, our ABN, and the ITC % on the policy and the vehicle.
    { p: 0, x: 257.5, y: 366.7, tick: true },                               // GST registered = Yes
    { p: 0, x: 350, y: 366.9, v: INSURED_ABN },                             // A.B.N.
    { p: 0, x: 400, y: 382.4, v: INSURED_ITC_PCT },                         // ITC on the policy %
    { p: 0, x: 400, y: 397.9, v: INSURED_ITC_PCT },                         // ITC on the vehicle %
    { p: 0, x: 132, y: 423.3, v: veh?.year },                               // Vehicle · Year
    { p: 0, x: 210, y: 423.3, v: veh?.make },                               // Vehicle · Make
    { p: 0, x: 370, y: 423.3, v: veh?.model },                              // Vehicle · Model
    { p: 0, x: 90,  y: 438.8, v: 'Van' },                                   // Body Type (per the schedule)
    { p: 0, x: 335, y: 438.8, v: c.rego },                                  // Registration No.
    { p: 0, x: 200, y: 547.3, v: ownedByUs ? INSURED_NAME : '' },           // Registered owner
    { p: 0, x: 100, y: 588.3, v: c.driverName || driver },                  // Driver's Name
    { p: 0, x: 402, y: 588.3, v: drv?.mobile },                             // Driver's Phone No.
    { p: 0, x: 110, y: 603.8, v: dAddr.street },                            // Driver's Address
    { p: 0, x: 460, y: 619.3, v: dAddr.postcode },                          // Driver's Postcode
    // Consent: the driver is engaged by us and was on their delivery round in our van.
    { p: 0, x: 275, y: 650.1, tick: true },                                 // used with consent = Yes
    { p: 0, x: 220, y: 665.8, v: 'Business — parcel delivery round' },      // Reason for use
    { p: 0, x: 170, y: 681.3, v: drv?.company_name ? `Contractor (${drv.company_name})` : 'Contractor' },
    { p: 0, x: 245, y: 696.8, v: 'Daily — this is the driver’s assigned vehicle' },
    // ── Page 2 — the accident ──
    { p: 1, x: 118, y: 40.7,  v: ddmmyyyy(c.incidentDate) },                // Date of occurrence
    { p: 1, x: 238, y: 40.7,  v: t.time },                                  // Time of Loss
    { p: 1, x: 330, y: 40.7,  v: t.ampm },                                  // am/pm
    { p: 1, x: 80,  y: 56.2,  v: loc.street || c.location },                // Location
    { p: 1, x: 458, y: 71.7,  v: loc.postcode },                            // Location postcode
    { p: 1, x: 95,  y: 242.2, v: tp.rego },                                 // TP1 Reg. No.
    // Road conditions: only "Day" is asserted, and only from the clock — a midday loss
    // is daylight by arithmetic, not assumption. Wet/dry and sealed/unsealed are left
    // blank because the driver never said, and a guess here is a misstatement.
    ...(/^(0?[7-9]|1[0-6]):/.test(c.incidentTime || '') ? [{ p: 1, x: 329, y: 429.6, tick: true }] : []),
    { p: 1, x: 45,  y: 490,   v: faultAccount, wrap: 450, lines: 2, lh: 11 }, // at fault + why
    // Accident description — the free block between the "Describe events…" prompt and
    // the sketch panel at y≈211. wrap 450 keeps it inside the ruled box, whose right
    // edge sits at x≈498 (measured off the form's own Checklist rule).
    { p: 1, x: 45,  y: 118,   v: c.description, wrap: 450, lines: 8, lh: 11 },
    // "Describe what the vehicle was being used for at the time"
    { p: 1, x: 260, y: 445.2, v: 'Parcel delivery (business use)' },
    // ── Page 3 — damage, police, third party ──
    // "Describe the damage:" panel, right of the vehicle diagram (x 267→495, y 56→165).
    { p: 2, x: 269, y: 68, v: c.damage, wrap: 222, lines: 8, lh: 10 },
    { p: 2, x: 112, y: 300.0, v: c.policeRef },                             // Police Report No.
    { p: 2, x: 312, y: 300.0, v: c.policeNotified ? ddmmyyyy(c.incidentDate) : '' },
    { p: 2, x: 132, y: 553.9, v: tp.year },                                 // TP Vehicle · Year
    { p: 2, x: 220, y: 553.9, v: tp.make },                                 // TP · Make
    { p: 2, x: 380, y: 553.9, v: tp.model },                                // TP · Model
    { p: 2, x: 260, y: 569.4, v: tp.rego },                                 // TP Registration No.
    { p: 2, x: 100, y: 646.9, v: tp.name },                                 // TP Driver's Name
    // The name we collect at the scene is the other DRIVER, so their phone belongs on
    // the driver's row (y 693) — not the owner's row above it (y 631), which we leave
    // blank rather than assert the driver also owns the vehicle.
    { p: 2, x: 112, y: 693.0, v: tp.phone },                                // TP Driver's Home Phone No.
    { p: 2, x: 210, y: 755.0, v: tp.insurer },                              // Other Party's Insurance Company
    { p: 2, x: 412, y: 755.0, v: tp.policy },                               // Other Party's Policy No.
    // Checkboxes — only the ones we hold reliable data for. Everything else on the
    // form stays untouched rather than risk a wrong tick on an insurance document.
    // "Notified" and "attended" are separate questions: ringing 000 from the roadside
    // notifies them without a car necessarily turning up.
    { p: 2, x: 59.5, y: c.policeNotified ? 284.4 : 268.9, tick: true },     // Police notified Yes/No
    // Only assert attendance either way if we know they were notified at all —
    // otherwise leave the question blank rather than tick "No" on no information.
    ...(c.policeNotified
      ? [{ p: 2, x: c.policeAttended ? 180 : 206.5, y: 315.4, tick: true }] // Attended scene Yes/No
      : []),
  ];
}

// Draw the CLM002 overlay. Returns { bytes, filled, total }.
async function fillClm002(pdf, c, veh, drv) {
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const pages = pdf.getPages();
  const fields = clm002Fields(c, veh, drv);
  let filled = 0;

  const wrapText = (text, size, maxW) => {
    const words = String(text).split(/\s+/);
    const out = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) > maxW && line) { out.push(line); line = w; }
      else line = test;
    }
    if (line) out.push(line);
    return out;
  };

  for (const f of fields) {
    const page = pages[f.p];
    if (!page) continue;
    const H = page.getSize().height;

    if (f.tick) {
      page.drawText('X', { x: f.x, y: H - (f.y + BASE), size: 9, font, color: INK() });
      filled++;
      continue;
    }
    const val = f.v == null ? '' : String(f.v).trim();
    if (!val) continue;

    if (f.wrap) {
      const size = 8.5;
      let lines = wrapText(val, size, f.wrap);
      const max = f.lines || 6;
      if (lines.length > max) {
        lines = lines.slice(0, max);
        lines[max - 1] = `${lines[max - 1].slice(0, 70)}… (full details in the attached summary)`;
      }
      lines.forEach((ln, i) => {
        page.drawText(ln, { x: f.x, y: H - (f.y + BASE + i * (f.lh || 11)), size, font, color: INK() });
      });
      filled++;
    } else {
      page.drawText(val, { x: f.x, y: H - (f.y + BASE), size: 9, font, color: INK() });
      filled++;
    }
  }
  return { bytes: await pdf.save(), filled, total: fields.length };
}

// Does this look like the Allianz CLM002 (4-page A4)? Guards the overlay against
// being stamped onto some other document the admin uploaded by mistake.
function looksLikeClm002(pdf) {
  if (pdf.getPageCount() !== 4) return false;
  const { width, height } = pdf.getPage(0).getSize();
  return Math.abs(width - A4_W) < 6 && Math.abs(height - A4_H) < 6;
}

// Is this PDF the Allianz claim form? Lets the drawer file a pasted/dropped copy as the
// claim's Allianz form (the round-trip: download → add the sketch by hand → paste back)
// instead of dumping it in as a nameless "other" document. Returns false for anything
// unreadable, so a bad file just files as a normal attachment.
export async function isClm002Pdf(bytes) {
  try {
    const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
    return looksLikeClm002(pdf);
  } catch (_) {
    return false;
  }
}

// Inspect an uploaded PDF's fillable fields. [] when there is no AcroForm (the real
// CLM002 is flat → [], which is expected and fine).
export async function listPdfFields(bytes) {
  try {
    const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
    return pdf.getForm().getFields().map((f) => ({ name: f.getName(), type: f.constructor?.name || 'Field' }));
  } catch (_) {
    return [];
  }
}

// Values offered to a fillable form, keyed by our canonical names.
export function claimFieldValues(c, veh) {
  const tp = c.thirdParty || {};
  const t = splitAmPm(c.incidentTime);
  return {
    claimref: c.claimRef || '',
    insured: INSURED_NAME,
    policy: POLICY_NO,
    insurer: 'Allianz',
    rego: c.rego || '',
    registration: c.rego || '',
    year: veh?.year || '', make: veh?.make || '', model: veh?.model || '',
    driver: c.driverName || '', drivername: c.driverName || '',
    date: ddmmyyyy(c.incidentDate), time: t.time, ampm: t.ampm,
    location: c.location || '',
    description: c.description || '',
    damage: c.damage || '',
    injuries: c.injuries || '',
    police: c.policeNotified ? 'Yes' : 'No',
    policereport: c.policeRef || '',
    thirdpartyname: tp.name || '', thirdpartyphone: tp.phone || '',
    thirdpartyrego: tp.rego || '',
    thirdpartyvehicle: [tp.make, tp.model].filter(Boolean).join(' '),
    thirdpartyinsurer: tp.insurer || '', thirdpartypolicy: tp.policy || '',
  };
}

// Keyword → canonical value key, most-specific first (AcroForm path only).
const FIELD_KEYWORDS = [
  [/thirdparty.*name|otherdriver.*name|tpname/, 'thirdpartyname'],
  [/thirdparty.*phone|otherdriver.*phone|tpphone/, 'thirdpartyphone'],
  [/thirdparty.*rego|otherveh.*rego|tprego/, 'thirdpartyrego'],
  [/thirdparty.*veh|othervehicle|tpvehicle/, 'thirdpartyvehicle'],
  [/thirdparty.*insur|otherinsurer|tpinsurer/, 'thirdpartyinsurer'],
  [/thirdparty.*policy|otherpolicy|tppolicy/, 'thirdpartypolicy'],
  [/claim.*(no|num|ref)/, 'claimref'],
  [/policereport|reportno/, 'policereport'],
  [/policy.*(no|num|ref)|policynumber/, 'policy'],
  [/nameofinsured|insuredname/, 'insured'],
  [/insurer|underwriter/, 'insurer'],
  [/reg(o|istration|no|number)|numberplate|plate/, 'rego'],
  [/drivername|nameofdriver|insureddriver/, 'drivername'],
  [/driver/, 'driver'],
  [/dateofloss|dateofaccident|incidentdate|lossdate|dateofoccurrence|(^|_)date/, 'date'],
  [/ampm/, 'ampm'],
  [/timeofloss|timeofaccident|incidenttime|(^|_)time/, 'time'],
  [/location|placeof|whereoccur|suburb/, 'location'],
  [/damage/, 'damage'],
  [/injur|personsinjured/, 'injuries'],
  [/police/, 'police'],
  [/(^|_)year/, 'year'], [/(^|_)make/, 'make'], [/(^|_)model/, 'model'],
  [/description|circumstanc|howoccur|details|whathappen/, 'description'],
];

export function autoMapFields(fieldNames, claim, veh) {
  const values = claimFieldValues(claim, veh);
  const out = {};
  for (const raw of fieldNames) {
    const key = String(raw).toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [re, canon] of FIELD_KEYWORDS) {
      if (re.test(key)) {
        const v = values[canon];
        if (nonEmpty(v)) out[raw] = v;
        break;
      }
    }
  }
  return out;
}

// Fill the uploaded Allianz claim PDF from the claim.
//   1. If it has AcroForm fields → fill them (a future fillable revision).
//   2. Else if it's the flat CLM002 → draw our coordinate overlay.
//   3. Else → throw, so the admin isn't handed a silently-blank form.
// `veh` is the matching fleet vehicle (year/make/model), optional.
export async function fillAllianzForm(templateBytes, claim, veh, drv, mapping) {
  const pdf = await PDFDocument.load(templateBytes, { ignoreEncryption: true });

  let fields = [];
  try { fields = pdf.getForm().getFields(); } catch (_) { fields = []; }

  if (fields.length) {
    const map = mapping || autoMapFields(fields.map((f) => f.getName()), claim, veh);
    let filled = 0;
    for (const f of fields) {
      const name = f.getName();
      if (!(name in map)) continue;
      const val = String(map[name] ?? '');
      try {
        const type = f.constructor?.name || '';
        if (/TextField/.test(type)) { f.setText(val); filled++; }
        else if (/CheckBox/.test(type)) { if (/^(y|yes|true|1|on)$/i.test(val)) f.check(); else f.uncheck(); filled++; }
        else if (/Dropdown|OptionList/.test(type)) { try { f.select(val); filled++; } catch (_) { /* no such option */ } }
      } catch (_) { /* field won't take it */ }
    }
    try { pdf.getForm().updateFieldAppearances(); } catch (_) { /* best-effort */ }
    return { bytes: await pdf.save(), filled, total: fields.length, mode: 'acroform' };
  }

  if (looksLikeClm002(pdf)) {
    const r = await fillClm002(pdf, claim, veh, drv);
    return { ...r, mode: 'overlay' };
  }

  throw new Error('This PDF isn’t the Allianz CLM002 claim form and has no fillable fields — upload the Allianz “Motor Vehicle Claim Form”, or send the branded summary PDF instead.');
}

// base64 ↔ bytes (browser), chunked to avoid call-stack limits.
export function base64ToBytes(b64) {
  const bin = atob(b64 || '');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
export function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(binary);
}
